import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { environment } from '../../environments/environment';
import { PaymentStatusEnum } from '../enums/payment-status.enum';
import { HeaderErrorEnum } from '../enums/header-error.enum';
import { SubUrlsEnum } from '../enums/sub-urls.enum';
import { IPointing, IPointingDtoIn, IPointingDtoOut, IPointings, IPointingsDtoIn } from '../interfaces/garderie.interface';
import {
    INurserySettingDTOIn,
    INurserySettingsDTOIn,
    INurserySettingsDTOOut,
    ITotalOfPointingPerMonth,
    ITotalOfPointingPerMonthDtoIn,
} from '../interfaces/nursery.interface';
import { INurseryHistoryPayload } from '../interfaces/payloads/nursery.payloads';
import { formatDateYYYYMMDD } from '../utils/date.utils';

@Injectable({
    providedIn: 'root',
})
export class NurseryService {
    constructor(private http: HttpClient) {}
    // === NURSERY === //

    public getNurseryHistory(payload: INurseryHistoryPayload): Observable<IPointings> {
        let params: HttpParams = new HttpParams();

        if (payload.studentId) {
            params = params.set('viewing_student_id', payload.studentId);
        }

        params = params.set('from', payload.dateFrom ? payload.dateFrom : formatDateYYYYMMDD(new Date()));
        params = params.set('to', payload.dateTo ? payload.dateTo : formatDateYYYYMMDD(new Date()));
        if (payload.search && payload.search.length) {
            params = params.set('name', payload.search);
        }
        if (payload.paymentStatus?.length === 1) {
            params = params.set('paid', payload.paymentStatus[0] === PaymentStatusEnum.PAID ? 1 : 0);
        }
        if (payload.period) {
            params = params.set('period', payload.period);
        }
        if (payload.page) {
            params = params.set('page', payload.page);
        }

        return this.http
            .get<IPointingsDtoIn>(environment.wasoApi + SubUrlsEnum.NURSERY, { params })
            .pipe(
                map((result: IPointingsDtoIn) => {
                    const pointings: IPointing[] = [];
                    if (result?.pointings) {
                        result.pointings.map((p: IPointingDtoIn) => {
                            pointings.push(this.pointingFromBack(p));
                        });
                    }
                    return {
                        pointings,
                        paging: result.paging,
                    };
                })
            );
    }

    public getTotalOfPointingByMonth(selectedStudentId: number): Observable<ITotalOfPointingPerMonth[]> {
        let params: HttpParams = new HttpParams();
        if (selectedStudentId) {
            params = params.set('viewing_student_id', selectedStudentId);
        }
        return this.http
            .get<ITotalOfPointingPerMonthDtoIn>(environment.wasoApi + SubUrlsEnum.NURSERY_POINTINGS_BY_MONTH, {
                params: selectedStudentId ? params : null,
            })
            .pipe(map((result: ITotalOfPointingPerMonthDtoIn) => result.pointings));
    }

    public getNurserySettings(schoolId: number): Observable<INurserySettingsDTOIn> {
        const headers: HttpHeaders = new HttpHeaders();
        return this.http.get<INurserySettingsDTOIn>(
            environment.wasoApi + SubUrlsEnum.NURSERY_SETTINGS.replace('{{school_id}}', schoolId.toString()),
            {
                headers,
            }
        );
    }

    // encodage manuel
    public addNursery(nursery: IPointingDtoOut): Observable<IPointing> {
        const headers: HttpHeaders = new HttpHeaders().set(HeaderErrorEnum.OWN_ERROR, 'true');
        return this.http
            .post<IPointingDtoIn>(environment.wasoApi + SubUrlsEnum.NURSERY_ADD, nursery, { headers })
            .pipe(map((p: IPointingDtoIn) => this.pointingFromBack(p)));
    }

    public deleteNursery(id: number): Observable<void> {
        const headers: HttpHeaders = new HttpHeaders();
        return this.http.delete<null>(environment.wasoApi + SubUrlsEnum.NURSERY_DELETE.replace('{{pointing_id}}', id.toString()), {
            headers,
        });
    }

    public updateNurserySettings(settings: INurserySettingsDTOOut, pointingSettingId: number): Observable<INurserySettingDTOIn> {
        return this.http.put<INurserySettingDTOIn>(
            environment.wasoApi + SubUrlsEnum.NUSERY_UPDATE_SETTINGS.replace('{{pointing_setting_id}}', pointingSettingId.toString()),
            settings
        );
    }

    private pointingFromBack(p: IPointingDtoIn): IPointing {
        const date: string[] = p.date.split('/');
        const start: string[] = p.start_at.split(':');
        const end: string[] = p.end_at.split(':');
        return {
            id: p.id,
            date: new Date(+date[2], +date[1] - 1, +date[0]),
            start: new Date(+date[2], +date[1] - 1, +date[0], +start[0], +start[1]),
            end: new Date(+date[2], +date[1] - 1, +date[0], +end[0], +end[1]),
            parentId: p.parent_id,
            educationLevelId: p.education_level_id,
            duration: p.duration,
            price: p.price,
            student: p.student,
            parents: p.parents,
            paymentStatus: p.payment_status,
        };
    }
}
